import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import { useChampionships } from '../features/command-center/useChampionships.js'
import { bodyCoverage } from '../features/command-center/bodyCoverage.js'
import { REGISTRY_ENTITY_COUNT } from '../lib/bodyRegistryIndex.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './BodyCoverage.module.css'

/**
 * WHAT THE BOARD CANNOT OPEN — the reconciliation the Governing Bodies page
 * could never do, because it derives its cards from the registry and joins
 * records onto them. See lib/bodyRegistryIndex.js for why that direction makes a
 * cardless body invisible rather than merely absent.
 *
 * ⚠️ THERE IS NO COVERAGE BADGE ON THIS PAGE, AND THERE MUST NEVER BE ONE.
 * A percentage here would be read as "how covered are we", and the honest answer
 * has three different denominators — bodies with a card, bodies with records, and
 * bodies that exist in the world — of which this page can see the first two. A
 * green "100%" on first contact is how a reporting surface stops being believed,
 * and this one is being shipped specifically to rebuild that.
 *
 * ⚠️ THE QUEUE IS THE HEADLINE, NOT A FOOTNOTE. The number at the top is the
 * thing that is wrong. The reconciliation under it is the proof that the number
 * is not hiding anything.
 *
 * ⚠️ AND IT DOES NOT MATCH tools/audit-map-coverage.mjs EXACTLY, ON PURPOSE.
 * The audit canonicalizes aliases first (buildBodyMap), so "OFSAA" and "Ontario
 * Federation of School Athletic Associations" are ONE body there and TWO here.
 * This page reports the raw labels because a registry entry is keyed on a label:
 * adding one card for "OFSAA" leaves the other 30 records still unreachable. The
 * difference is stated on the page rather than reconciled away.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/* ⚠️ THE ASSERTED PHRASES LIVE HERE AS CONSTANTS, NOT AS JSX TEXT.
 * check-shipped-features.mjs greps the BUILT bundle, and the JSX transform
 * collapses and splits multi-line element text in ways that are not worth
 * predicting. A named constant survives the build as one literal, so the guard
 * asserts the sentence that actually ships rather than a fragment of it. */
export const NO_CARD_HEADLINE = 'governing bodies hold records and have no card'
export const RECONCILED_OK =
  'Every record in the object is in exactly one of the four rows above, so nothing has '
  + 'been silently dropped from this count.'
export const RECONCILE_FAILED = 'RECONCILIATION FAILED.'

/* The reason an absent number is absent, carried WITH the absence. Rendering a
 * bare zero for any of these is the defect this page was built against. */
const REP_UNAVAILABLE =
  'sr_assigned_rep is populated on a small fraction of the object. Every assignment '
  + 'made from the Command Center returned 400 for the whole period this field was '
  + 'meant to fill — the missing `reason` the ledger requires, fixed in 2.73.0 — so '
  + 'a blank here means the write never landed, NOT that nobody owns the body. The '
  + '2026-2027 breakdown sheet is the authority until the field refills.'

export default function BodyCoverage() {
  const navigate = useNavigate()
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const { data, status, error, complete, loadedCount } = useChampionships()
  const [open, setOpen] = useState(null)

  const cov = useMemo(() => bodyCoverage(data || []), [data])

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Reference" title="Body Coverage" />
        <div className="card">
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  const t = cov.totals
  const loading = status === 'loading'
  /* 🔴 THERE WAS NO ERROR BRANCH ON THIS PAGE AT ALL, AND THAT IS A LIE TO A
   * REP. The render tested `loading` and `partial` and nothing else, so a hard
   * read failure fell through into the `{!loading && (` body with an empty
   * dataset and drew the headline "0 bodies the board cannot open, covering 0
   * records" — a reassuring, confident, completely invented all-clear on the
   * one page whose entire job is to say what is missing. An empty board after
   * a failed read is the worst possible rendering of that failure, and this
   * page's own subtitle argues against it. */
  const failed = status === 'error'

  /* ⚠️ A PARTIAL LOAD MUST NOT BE REPORTED AS A RESULT. Until the cursor walk
   * finishes, every count here is an undercount, and an undercount of "bodies
   * with no card" is the reassuring direction to be wrong in. */
  const partial = !loading && !complete

  const row = (r) => {
    const isOpen = open === r.body
    return (
      <div key={r.body} className={styles.row} data-open={isOpen ? '1' : '0'}>
        <button type="button" className={styles.rowHead} onClick={() => setOpen(isOpen ? null : r.body)}
          aria-expanded={isOpen}>
          <span className={styles.count}>{nf(r.count)}</span>
          <span className={styles.body}>{r.body}</span>
          <span className={styles.seg}>{r.segment}</span>
          <span className={styles.rep}>
            {r.reps.length
              ? r.reps.map((x) => `${x.rep} (${nf(x.count)})`).join(', ')
              : <em className={styles.muted}>no rep on the record</em>}
          </span>
          <span className={styles.chev}>{isOpen ? '▾' : '▸'}</span>
        </button>
        {isOpen && (
          <div className={styles.detail}>
            <p className={styles.meta}>
              {r.crowned > 0
                ? <><b>{nf(r.crowned)}</b> of these already have a champion recorded. </>
                : 'No champion is recorded on any of these. '}
              {r.states.length
                ? <>States on the records: {r.states.join(', ')}. </>
                : 'No state is recorded on any of these. '}
              {r.segments.length > 1 && (
                <>Segments: {r.segments.map(([s, n]) => `${s} (${nf(n)})`).join(', ')}. </>
              )}
              {r.outOfScope && (
                <><b>This is assigned elsewhere, not missing a card.</b> Its top segment is one the
                2026-2027 breakdown routes elsewhere. </>
              )}
            </p>
            {r.truncated && (
              <p className={styles.meta}>
                Showing the first {nf(r.records.length)} of {nf(r.count)} records.
              </p>
            )}
            {/* ⚠️ FIVE COLUMNS AT 12px IN 390px OF SCREEN. Measured on the
                shipped bundle: this table was still side-by-side at phone
                width, inside a module with no `@media` query and no
                `overflow-x` wrapper, so each column got roughly 60px — and
                the Champion column, which is the payload, was crushed to
                nothing. `cc-cards` stacks it with a label per cell. */}
            <table className={`${styles.table} cc-cards`}>
              <thead>
                <tr><th>Championship</th><th>Sport</th><th>State</th><th>Date</th><th>Champion</th></tr>
              </thead>
              <tbody>
                {r.records.map((c) => (
                  /* ⚠️ A REAL RECORD ROW, SO IT GETS A DESTINATION — NOT AN
                   * aria-disabled DECLARATION. The other tables on this page are
                   * count readouts; this one carries a championship's name,
                   * sport, state, date and champion, and the obvious thing to do
                   * with it is open it. Nothing on this page had any way to.
                   *
                   * It reuses the `?record=` deep link — the same door
                   * MyWorklist uses, verified end to end: the Command Center
                   * consumes the param and opens the drawer, waiting for the
                   * universe if the id lands first. A second drawer here would
                   * be a second thing to drift. */
                  <tr key={c.id}>
                    <td data-col="name" data-label="Championship">
                      <button
                        type="button"
                        className={styles.openRecord}
                        onClick={() => navigate(`/command-center?record=${encodeURIComponent(c.id)}`)}
                      >
                        {c.name} →
                      </button>
                    </td>
                    <td data-label="Sport">{c.sport || <span className={styles.muted}>—</span>}</td>
                    <td data-label="State">{c.state || <span className={styles.muted}>—</span>}</td>
                    <td data-label="Date">
                      {c.dateRaw || <span className={styles.muted}>none</span>}
                      {/* ⚠️ A PROJECTED DATE NEVER RENDERS AS A FACT. */}
                      {c.dateRaw && !c.dateTrusted && <span className={styles.tag}> projected</span>}
                    </td>
                    <td data-label="Champion">{c.champion || <span className={styles.muted}>not crowned</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Reference"
        title="Body Coverage"
        subtitle="Every governing body named on a record, and whether the board can open it. A body with records and no registry entry renders nowhere in this product — not as an empty card, not as a zero. It is simply absent, from every direction at once."
      />

      {loading && (
        <div className="card">
          <p>Loading the object — {nf(loadedCount)} records so far. Nothing below is counted until the walk finishes.</p>
        </div>
      )}

      {failed && (
        <div className="card">
          <p className={styles.bad}>
            <b>The championship feed could not be read, so there is no coverage answer below.</b>
          </p>
          <p className={styles.muted}>{String(error?.message || error || 'No reason was returned.')}</p>
          <p className={styles.muted}>
            This is a failed read, not a clean board. Before this branch existed the page drew
            its headline from an empty dataset and reported zero bodies the board cannot open —
            which on the one surface built to say what is missing is the most damaging thing it
            could have said. Reload before acting on anything here.
          </p>
        </div>
      )}

      {!loading && !failed && (
        <>
          {/* ---- THE QUEUE, AS THE HEADLINE ---------------------------- */}
          <section className={`card ${styles.headline}`}>
            <div className={styles.big}>{nf(t.noCardBodies)}</div>
            <div className={styles.bigLabel}>
              {NO_CARD_HEADLINE}, covering{' '}
              <b>{nf(t.noCard)}</b> records.{' '}
              {/* ⚠️ "NOTHING IN THIS PRODUCT CAN REACH THEM" SHIPPED HERE FROM
                  2.74.0 AND IS NO LONGER TRUE, as of 2.112.0: every one of these
                  labels is now a card on the Governing Bodies page, derived from
                  the object instead of from the registry. Leaving the old
                  sentence up would have been this page overstating a defect to
                  keep its own headline — the mirror image of a false green, and
                  just as corrosive to a surface built to be believed. What has
                  NOT changed is the registry, so the count stands. */}
              They are reachable now: the <Link to="/governing-bodies">Governing Bodies</Link>{' '}
              page cards every one of them on its <b>No registry entry</b> tab, classified, with
              the arithmetic. The registry itself still does not hold them, which is why this
              count has not moved.
            </div>
            {partial && (
              <p className={styles.warn} role="alert">
                THE OBJECT IS NOT FULLY LOADED. These are undercounts, and the
                direction of the error is reassuring. Reload before acting on them.
              </p>
            )}
          </section>

          {/* ---- THE RECONCILIATION ------------------------------------ */}
          <section className={`card ${styles.section}`}>
            <h2 className={styles.h2}>It adds up, or it says so</h2>
            <table className={styles.recon}>
              <tbody>
                <tr>
                  <td className={styles.n}>{nf(t.onCard)}</td>
                  <td>on a card — <b>{nf(t.onCardBodies)}</b> body labels reach one of the{' '}
                    {REGISTRY_ENTITY_COUNT} registry entities</td>
                </tr>
                <tr data-flag="1">
                  <td className={styles.n}>{nf(t.noCard)}</td>
                  <td>no card — <b>{nf(t.noCardBodies)}</b> body labels the registry does not hold.
                    <b> Fix: add the registry entry.</b></td>
                </tr>
                <tr>
                  <td className={styles.n}>{nf(t.unspecified)}</td>
                  <td>the record says <code>Unspecified</code>. Not an organisation, so no
                    registry entry can ever help. <b>Fix: the data, not the board.</b></td>
                </tr>
                <tr>
                  <td className={styles.n}>{nf(t.blank)}</td>
                  <td>the governing-body field is empty. Same fix as above, and kept apart
                    from <code>Unspecified</code> because one was typed and the other never was.</td>
                </tr>
                <tr className={styles.sum}>
                  <td className={styles.n}>{nf(t.accounted)}</td>
                  <td>
                    accounted for, of <b>{nf(t.universe)}</b> records loaded.
                  </td>
                </tr>
              </tbody>
            </table>
            {cov.reconciled ? (
              <p className={styles.ok}>Reconciled. {RECONCILED_OK}</p>
            ) : (
              <p className={styles.fail} role="alert">
                <strong>{RECONCILE_FAILED}</strong> {nf(Math.abs(cov.shortfall))} records
                are {cov.shortfall > 0 ? 'missing from' : 'double-counted across'} the four rows.
                Do not act on the buckets above until this is explained.
              </p>
            )}
            <p className={styles.note}>
              <b>This page and <code>tools/audit-map-coverage.mjs</code> count differently, on
              purpose.</b> The audit merges aliases first, so <i>OFSAA</i> and <i>Ontario
              Federation of School Athletic Associations</i> are one body there and two here.
              A registry entry is keyed on a label, so the labels are what this page counts:
              adding a card for one of those two leaves the other still unreachable.
            </p>
          </section>

          {/* ---- THE PAC-12 SHAPE ------------------------------------- */}
          <section className={`card ${styles.section}`}>
            <h2 className={styles.h2}>
              No card, and a rep is assigned — {nf(cov.noCardWithRep.length)} bodies,{' '}
              {nf(t.noCardWithRep)} records
            </h2>
            <p className={styles.note}>
              This is the exact shape of the Pac-12: a rep is accountable for work the
              product will not show them. {REP_UNAVAILABLE}
            </p>
            {cov.noCardWithRep.length
              ? <div className={styles.rows}>{cov.noCardWithRep.map(row)}</div>
              : <p className={styles.note}>None — but read the paragraph above before taking
                that as good news. The rep field being empty and nobody owning the body look
                identical here, and only one of them is true.</p>}
          </section>

          {/* ---- THE REST --------------------------------------------- */}
          <section className={`card ${styles.section}`}>
            <h2 className={styles.h2}>
              No card, no rep on the record — {nf(cov.noCardNoRep.length)} bodies,{' '}
              {nf(t.noCardNoRep)} records
            </h2>
            <p className={styles.note}>
              Ranked by how much is behind them. A row marked <b>assigned elsewhere</b> is not an
              assignment gap: its segment is routed to a different line entirely, and
              calling it unassigned would cry wolf on thousands of rows.
            </p>
            <div className={styles.rows}>{cov.noCardNoRep.map(row)}</div>
          </section>
        </>
      )}
    </>
  )
}
